<template>
  <el-form
    v-loading="isLoading"
    :model="shipperSetting"
    size="small"
  >
    <el-alert
      type="info"
      :closable="false"
      class="mb-3"
    >
      Underlined fields will also affect the Open360 platform
    </el-alert>
    <el-row :gutter="15">
      <el-col :md="10">
        <h2>Printer Settings</h2>
        <br>
        <p>Here you can configure your printing preferences. We support direct thermal label (ZPL) printing via the tray application and PDF printing without it.</p>
        <br>
        <p>Please note that the legacy Java plugin is no longer supported and you must updated to the tray application for thermal label printing.</p>
      </el-col>
      <el-col :md="14">
        <el-form-item
          label="Printer Type"
          class="open360"
        >
          <el-select
            v-model="shipperSetting.printer_type"
            placeholder="Printer Type"
            class="w-100"
            clearable
          >
            <el-option
              label="PDF (Default)"
              :value="0"
            />
            <el-option
              label="Thermal Printing (Tray Applications)"
              :value="10"
            />
            <el-option
              label="Thermal Printing (Cloud Print)"
              :value="20"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          label="Thermal Label Printer Name"
          class="open360"
        >
          <el-input
            v-model="shipperSetting.printer_name"
            placeholder="Thermal Label Printer Name"
            clearable
          />
        </el-form-item>
        <el-form-item>
          <el-tooltip
            effect="dark"
            placement="right"
          >
            <div slot="content">
              The default left hand margin used when printing via Thermal
            </div>
            <span style="color: #606266;">Printer Margin</span>
          </el-tooltip>
          <el-slider v-model="shipperSetting.printer_margin" />
        </el-form-item>
        <el-form-item>
          <span style="color: #606266;">Printer Darkness</span>
          <el-slider
            v-model="shipperSetting.printer_darkness"
            :max="30"
          />
        </el-form-item>
        <el-form-item>
          <span style="color: #606266;">Print Breaker Label</span>
          <el-select
            v-model="shipperSetting.print_breaker_label"
            placeholder="Print Breaker Label"
            class="w-100"
            clearable
          >
            <el-option
              label="No (Default)"
              :value="false"
            />
            <el-option
              label="Yes"
              :value="true"
            />
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>
    <hr>
    <el-row :gutter="15">
      <el-col :md="10">
        <h2>Consignment Preferences</h2>
        <br>
        <p>The consignment preference options give you control on default actions during the consignment generation and manifesting process.</p>
        <br>
        <p>You can control everything from automatically consolidating consignments with matching origins/destinations, to generating and sending notifications to your customers of parcel despatch.</p>
      </el-col>
      <el-col :md="14">
        <el-form-item
          label="Default Special Instructions"
          class="open360"
        >
          <el-input
            v-model="shipperSetting.default_special_instructions"
            type="textarea"
            :rows="4"
            placeholder="Default Special Instructions"
          />
        </el-form-item>
        <el-form-item
          label="Default Service"
          class="open360"
        >
          <el-select
            v-model="shipperSetting.user_def_service"
            placeholder="Select a Carrier Service"
            class="w-100"
            :loading="isLoadingCarrierServices"
            clearable
            @clear="shipperSetting.user_def_service = null"
          >
            <el-option
              v-for="cs in carrierServices"
              :key="cs.id"
              :label="cs.label"
              :value="cs.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          label="Default Address"
          class="open360"
        >
          <el-select
            v-model="shipperSetting.address"
            filterable
            remote
            placeholder="Default Address"
            value-key="id"
            clearable
            class="w-100"
            :remote-method="remoteMethodAddress"
            :loading="isLoadingAddresses"
            @clear="shipperSetting.address = null"
          >
            <el-option
              v-for="item in addresses"
              :key="item.id"
              :label="`${item.name || '-'}, ${item.suburb || '-'}, ${item.state}, ${item.postcode}`"
              :value="item"
            />
          </el-select>
        </el-form-item>
        <h2 style="margin-top: 35px;">
          <u>Customer Configurable</u>
        </h2>
        <el-row :gutter="15">
          <el-col :md="12">
            <el-form-item label="Auto-Save Address Book">
              <el-switch v-model="shipperSetting.autosave_addressbook" />
            </el-form-item>
            <el-form-item label="Cost Centre Required">
              <el-switch v-model="shipperSetting.costcentre_required" />
            </el-form-item>
            <el-form-item
              label="Reference Field Required"
              class="open360"
            >
              <el-switch v-model="shipperSetting.reference_required" />
            </el-form-item>
            <el-form-item
              label="Internal Reference Field Required"
              class="open360"
            >
              <el-switch v-model="shipperSetting.user_reqinternalref" />
            </el-form-item>
            <el-form-item
              label="Quote Reference Field Required"
              class="open360"
            >
              <el-switch v-model="shipperSetting.user_quote_ref_req" />
            </el-form-item>
          </el-col>
          <el-col :md="12">
            <el-form-item
              label="Enable Shipping Notifications"
              class="open360"
            >
              <el-switch v-model="shipperSetting.send_despatch_emails" />
            </el-form-item>
            <el-form-item
              label="Enable Consignment Consolidation"
              class="open360"
            >
              <el-switch v-model="shipperSetting.consolidate_cons" />
            </el-form-item>
            <el-form-item label="Enable Barcode Scanning">
              <el-switch v-model="shipperSetting.barcode_scanning" />
            </el-form-item>
            <el-form-item label="Signature Required">
              <el-switch v-model="shipperSetting.user_reqsignature" />
            </el-form-item>
          </el-col>
        </el-row>
        <h2 style="margin-top: 30px;">
          <u>Admin Only Configurable</u>
        </h2>
        <el-row :gutter="15">
          <el-col :md="12">
            <el-tooltip
              effect="dark"
              placement="right"
            >
              <div slot="content">
                Block creation of consignments with reused reference numbers. Only applicable to consignments created on this team
              </div>
              <el-form-item label="Reference Must Be Unique">
                <el-switch v-model="shipperSetting.reference_unique" />
              </el-form-item>
            </el-tooltip>
            <el-form-item label="Volume (m3) Entry Allowed">
              <el-switch v-model="shipperSetting.can_enter_volume" />
            </el-form-item>
            <el-form-item label="Supplier Login Only">
              <el-switch v-model="shipperSetting.supplier_only" />
            </el-form-item>
          </el-col>
          <el-col :md="12">
            <el-form-item
              label="Can Consign"
              class="open360"
            >
              <el-switch v-model="shipperSetting.can_consign" />
            </el-form-item>
            <el-form-item
              label="Allow Third Party"
              class="open360"
            >
              <el-switch v-model="shipperSetting.allow_third_party" />
            </el-form-item>
            <el-form-item
              label="Allow Importing (Polling)"
              class="open360"
            >
              <el-switch v-model="shipperSetting.allow_import" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
    <hr>
    <el-row :gutter="15">
      <el-col :md="10">
        <h2>Pricing</h2>
        <br>
        <p>The pricing options allow you to control pricing settings.</p>
      </el-col>
      <el-col :md="14">
        <el-row :gutter="15">
          <el-col :md="12">
            <el-form-item label="Enable and Display Rates & Quoting">
              <el-switch v-model="shipperSetting.show_rates" />
            </el-form-item>
            <el-form-item label="Dimensions Required">
              <el-switch v-model="shipperSetting.dimensions_required" />
            </el-form-item>
            <el-form-item label="Quoting Inclusive of Tax">
              <el-switch v-model="shipperSetting.quoting_inclusive" />
            </el-form-item>
          </el-col>
          <el-col :md="12">
            <el-form-item label="Do Not Display Pricing">
              <el-switch v-model="shipperSetting.hide_pricing" />
            </el-form-item>
            <el-form-item label="Calculate Items as Totals">
              <el-switch v-model="shipperSetting.calculation_method" />
            </el-form-item>
            <el-form-item label="Lowest Shipping Cost Only">
              <el-switch v-model="shipperSetting.lowest_cost_only" />
            </el-form-item>
            <el-form-item label="Justify Manual Carrier Selection">
              <el-switch v-model="shipperSetting.justify_manual_carrier_overrides" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
    <hr>
    <el-row :gutter="15">
      <el-col :md="10">
        <h2>Address Search</h2>
        <br>
        <p>Controls Auto Fill of Receiver Line 1 using a third party platform</p>
      </el-col>
      <el-col :md="14">
        <el-col :md="12">
          <el-form-item label="Google Maps (Worldwide)">
            <el-switch v-model="shipperSetting.google_address_search" />
          </el-form-item>
        </el-col>
        <el-col :md="12">
          <el-form-item label="NZ Post (New Zealand)">
            <el-switch v-model="shipperSetting.nzpost_address_search" />
          </el-form-item>
        </el-col>
      </el-col>
    </el-row>
    <hr>
    <el-row :gutter="15">
      <el-col :md="10">
        <h2>Misc</h2>
        <br>
        <p>Control other fields that don't fit in anywhere else.</p>
      </el-col>
      <el-col :md="14">
        <el-form-item
          label="Shipper Settings Level"
          class="open360"
        >
          <el-select
            v-model="shipperSetting.shipper_settings_level"
            placeholder="Shipper Settings Level"
            class="w-100"
            clearable
          >
            <el-option
              label="Full Account & User Settings"
              :value="2"
            />
            <el-option
              label="User Settings & Address Book"
              :value="1"
            />
            <el-option
              label="Disable User Settings"
              :value="0"
            />
          </el-select>
        </el-form-item>
        <el-row :gutter="15">
          <el-col :md="12">
            <el-tooltip
              effect="dark"
              placement="left"
            >
              <div slot="content">
                Previously Show Only Users Cons
              </div>
              <el-form-item
                label="Only Show Consignments Created on This Team"
                class="open360"
              >
                <el-switch v-model="shipperSetting.show_only_users_cons" />
              </el-form-item>
            </el-tooltip>
            <el-tooltip
              effect="dark"
              placement="left"
            >
              <div slot="content">
                Previously Show Only User Imports
              </div>
              <el-form-item
                label="Only Show Imports Created on This Team"
                class="open360"
              >
                <el-switch v-model="shipperSetting.show_only_users_imports" />
              </el-form-item>
            </el-tooltip>
            <el-form-item
              label="Show Invoices"
              class="open360"
            >
              <el-switch v-model="shipperSetting.show_invoices" />
            </el-form-item>
            <el-form-item
              label="Show Reports"
              class="open360"
            >
              <el-switch v-model="shipperSetting.show_reports" />
            </el-form-item>
            <el-tooltip
              effect="dark"
              placement="right"
            >
              <div slot="content">
                Enable Manifests on Open360. Leave disabled if team is not using Open360.
              </div>
              <el-form-item
                label="Show Manifests"
                class="open360"
              >
                <el-switch v-model="shipperSetting.user_manifest" />
              </el-form-item>
            </el-tooltip>
            <el-tooltip
              effect="dark"
              placement="left"
            >
              <div slot="content">
                Enable Enquiries on Open360. Leave disabled if team is not using Open360.
              </div>
              <el-form-item
                label="Show Issues"
                class="open360"
              >
                <el-switch v-model="shipperSetting.show_issues" />
              </el-form-item>
            </el-tooltip>
            <el-tooltip
              effect="dark"
              placement="left"
            >
              <div slot="content">
                Enable Reconciliation on Open360. Leave disabled if team is not using Open360 or is not a SaaS customer.
              </div>
              <el-form-item
                label="Show Reconciliation"
                class="open360"
              >
                <el-switch v-model="shipperSetting.reconciliation_enabled" />
              </el-form-item>
            </el-tooltip>
          </el-col>
          <el-col :md="12">
            <el-tooltip
              effect="dark"
              placement="right"
            >
              <div slot="content">
                Previously Search Only Users Cons
              </div>
              <el-form-item
                label="Only Search for Consignments Created on This Team"
                class="open360"
              >
                <el-switch v-model="shipperSetting.search_only_users_cons" />
              </el-form-item>
            </el-tooltip>
            <el-form-item
              label="Tracking Only"
              class="open360"
            >
              <el-switch v-model="shipperSetting.tracking_only" />
            </el-form-item>

            <el-tooltip
              effect="dark"
              placement="right"
            >
              <div slot="content">
                Enabling this only shows consignments that match the configured sender address when searching or in the Manifest and Manage tab. Can only be used when a default address is set on the team.
              </div>
              <el-form-item
                label="Only Show Consignments From Sender Address"
                class="open360"
              >
                <el-switch v-model="shipperSetting.user_show_same_sender" />
              </el-form-item>
            </el-tooltip>
            <el-tooltip
              effect="dark"
              placement="right"
            >
              <div slot="content">
                Enabling this setting disables the 20,000kg and 34.56m3 consignment limits usually enforced when consigning and quoting.
              </div>
              <el-form-item label="Bypass Cubic Limit">
                <el-switch v-model="shipperSetting.bypass_cubic_limit" />
              </el-form-item>
            </el-tooltip>
            <el-tooltip
              effect="dark"
              placement="right"
            >
              <div slot="content">
                Enabling this will the default sender Business/Residential option to Residential instead of Business.
              </div>
              <el-form-item label="Receiver Default Residential">
                <el-switch v-model="shipperSetting.default_residential" />
              </el-form-item>
            </el-tooltip>
            <el-tooltip
              effect="dark"
              placement="right"
            >
              <div slot="content">
                Remove the dashboard from OpenFreight. The consign page will be the default page for the team when this is enabled.
              </div>
              <el-form-item
                label="Disable Dashboard"
                class="open360"
              >
                <el-switch v-model="shipperSetting.user_no_dashboard" />
              </el-form-item>
            </el-tooltip>
            <el-tooltip
              effect="dark"
              placement="right"
            >
              <div slot="content">
                Enables consignments to be re-allocated to child accounts. It is recommended to avoid changing this.
              </div>
              <el-form-item label="Enable Moving Consignments Between Accounts">
                <el-switch v-model="shipperSetting.user_assign_consignment" />
              </el-form-item>
            </el-tooltip>
          </el-col>
        </el-row>
        <br>
        <el-form-item style="float: right;">
          <el-button
            type="primary"
            :loading="isLoading"
            @click.native="save"
          >
            Save
          </el-button>
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
export default {
  props: {
    accountId: {
      type: String,
      required: true
    },
    teamId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      shipperSetting: {},
      carrierServices: [],
      isLoadingCarrierServices: true,
      isLoading: false,
      addresses: [],
      isLoadingAddresses: false
    }
  },
  created () {
    this.loadForm()
    this.loadServices()
  },
  methods: {
    loadForm () {
      this.isLoading = true
      this.$sdk.team.read(this.teamId, builder => {
        builder.context = { resource: 'account', id: this.accountId }
        builder.include = [ 'shipper_setting.address', 'shipper_setting.default_service.carrier' ]
      }).then(res => {
        this.shipperSetting = res.shipper_setting
        if (this.shipperSetting.address instanceof this.$sdk.account_location) {
          this.loadAccountLocationByID(this.shipperSetting.address.id)
        }
      }).finally(() => {
        this.isLoading = false
      })
    },
    loadAccountLocationByID (id) {
      this.isLoadingAddresses = true
      this.$sdk.account_location.read(id, builder => {
        builder.context = { resource: 'account', id: this.accountId }
      }).then(res => {
        this.addresses = [res]
      }).catch(() => {
        this.addresses = []
      }).finally(() => { this.isLoadingAddresses = false })
    },
    loadServices () {
      this.$sdk.service.list(builder => {
        builder.include = ['carrier']
        builder.filters = { account_carrier_service_setting_strict_account: this.accountId }
        builder.page = 1
        builder.limit = 100
        builder.order = 'uuid:asc'
      }).then(res => {
        const services = [
          {
            id: -1,
            label: 'Quote All Available',
            value: 'quote all available'
          },
          {
            id: -2,
            label: 'Fastest Available',
            value: 'fastest available'
          },
          {
            id: 0,
            label: 'Lowest Cost Available',
            value: 'lowest cost available'
          }
        ]

        res.map(service => {
          services.push({
            id: service.id,
            label: `${service.carrier.name}, ${service.name}, ${service.chainit}`,
            value: service.id
          })
        })

        this.carrierServices = services
      })
        .finally(() => {
          this.isLoadingCarrierServices = false
        })
    },
    remoteMethodAddress (query) {
      this.isLoadingAddresses = true
      this.$sdk.account_location.list(builder => {
        builder.context = { resource: 'account', id: this.accountId }
        builder.page = 1
        builder.limit = 100
        builder.filters = { account_location_find: query }
        builder.order = 'name:asc'
      }).then(res => {
        this.addresses = res
      }).catch(() => {
        this.addresses = []
      }).finally(() => { this.isLoadingAddresses = false })
    },
    handleSelectAddress (address) {
      this.shipperSetting.address = address
    },
    formatAddress (address) {
      if (address) return `${address.name || '-'}, ${address.suburb || '-'}, ${address.state || '-'}, ${address.postcode || '-'}`
      return ''
    },
    save () {
      this.isLoading = true
      this.shipperSetting.update(builder => {
        builder.include = [ 'address', 'default_service.carrier' ]
      })
        .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>

<style>
.open360 label {
  text-decoration: underline blue;
}
</style>
